// @givmo/employer-module — what approving each waiting match request would match NOW (EP-WEB41).
//
// A waiting request's figure is what is left for it in request order (EP-BE19, D-EP-56): the budgets
// after the approved matches and the EARLIER waiting requests. So it moves when an earlier request is
// decided or withdrawn elsewhere — another admin, an app, the employee — and the list it was read from
// goes stale in EITHER direction. Declining an earlier $150 request frees its share for the next
// (Jonathan, 2026-10-05 15:14: the later request's match "should go from $100 to $150"). The cap
// routes answer `min(asked, allowed)`, so a dialog that asks with the listed figure can only confirm
// it or go lower, and books the LOWER match.
//
// So the approve dialog reads every waiting request's figure again when it opens, from the read that
// serves them all: `GET /admin/company/matches/pending`, the apps' list (`company_info:read`, a
// baseline string every company admin holds; the backend's `COMPANY_BASELINE_MEMBER_PERMISSIONS`).
// Its `approvable` is the dashboard's, computed by the function the approve route enforces, and it
// lists every waiting ordinary request whole, with no page cut. A request is found here by its legs'
// match ids — the set the approve routes act on — never by a post. The route is a `-> List[Dict]`
// one, so its rows are read by hand off `unknown`.
//
// The read never blocks an approval. It is bounded as the sign-up door's employer read is
// (`employer-of-record.ts`), and a failed, slow or unreadable answer is `null`: the dialog then does
// what it did before, opening on the listed figure and re-checking it.

import { extractList, sdk } from '@givmo/api-client';
import {
  chargeableCents,
  formatCentsUsd,
  readMatchFigure,
  type ApprovalGroup,
  type MatchFigure,
} from './dashboard-decode';

/** How long the dialog waits on the read before it opens on the listed figure (the door's bound). */
export const CURRENT_FIGURES_TIMEOUT_MS = 8_000;

/** A request's identity here: its legs' match ids, in any order. */
export function matchSetKey(matchIds: readonly string[]): string {
  return [...matchIds].sort().join(',');
}

/**
 * Each waiting request's figure from `/pending`'s body, keyed by {@link matchSetKey}. A row whose
 * `match_ids` do not read is left out, and so is a match set two rows share: a figure that cannot be
 * tied to exactly one request is never used.
 */
export function readCurrentFigures(body: unknown): Map<string, MatchFigure> {
  const figures = new Map<string, MatchFigure>();
  const shared = new Set<string>();
  for (const row of extractList<unknown>(body)) {
    if (row === null || typeof row !== 'object') continue;
    const ids: unknown = (row as { match_ids?: unknown }).match_ids;
    if (!Array.isArray(ids) || ids.length === 0) continue;
    if (!ids.every((id): id is string => typeof id === 'string' && id.length > 0)) continue;
    const key = matchSetKey(ids);
    if (figures.has(key) || shared.has(key)) {
      figures.delete(key);
      shared.add(key);
      continue;
    }
    figures.set(key, readMatchFigure((row as { approvable?: unknown }).approvable));
  }
  return figures;
}

/** Every waiting request's current figure, or `null` when the read fails or does not answer in time. */
export async function fetchCurrentFigures(): Promise<Map<string, MatchFigure> | null> {
  const read = (async () => {
    const { data } = await sdk.donationMatchesGetPendingMatchesGrouped({ throwOnError: true });
    return readCurrentFigures(data);
  })();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const unanswered = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), CURRENT_FIGURES_TIMEOUT_MS);
  });
  try {
    return await Promise.race([read.catch(() => null), unanswered]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** Approve all at the figures approving its requests would match now: what its dialog states and charges. */
export interface CurrentApproveAll {
  /** The units, oldest request first, each at its figure now. */
  groups: ApprovalGroup[];
  /** Waiting requests left out: nothing is left for them now. */
  leftOut: number;
  /** Anything other than the list showed: a unit's figure, which requests are units, or how many
   *  are left out. */
  changed: boolean;
}

/**
 * Approve all over the requests the list showed, each at what approving it would match NOW. A request
 * the read lists takes its figure: a unit while something is left for it, else left out. One it no
 * longer lists is no longer waiting — decided or withdrawn since the list loaded — so it is neither:
 * charging it would only meet its approve route's refusal part-way through the batch. A read that
 * lists NONE of them is no answer, and the list stands, as when the read fails; the charge loop's own
 * cap check stays the guard either way. `requests` is every waiting request the list showed, oldest
 * first (a left-out one with `amountValue` null); without it the units alone are re-read, and the
 * list's `leftOut` stands for the rest.
 */
export function currentApproveAll(
  listed: {
    groups: readonly ApprovalGroup[];
    leftOut?: number;
    requests?: readonly ApprovalGroup[];
  },
  figures: ReadonlyMap<string, MatchFigure> | null,
): CurrentApproveAll {
  const listedLeftOut = listed.leftOut ?? 0;
  const requests = listed.requests ?? listed.groups;
  if (figures === null || !requests.some((r) => figures.has(matchSetKey(r.matchIds)))) {
    return { groups: [...listed.groups], leftOut: listedLeftOut, changed: false };
  }
  const groups: ApprovalGroup[] = [];
  let leftOut = listed.requests === undefined ? listedLeftOut : 0;
  for (const request of requests) {
    const figure = figures.get(matchSetKey(request.matchIds));
    if (figure === undefined) continue;
    if (figure.kind === 'available') {
      groups.push({
        ...request,
        amountValue: figure.cents / 100,
        amountLabel: formatCentsUsd(figure.cents),
      });
    } else if (figure.kind === 'used-up' || request.amountValue === null) {
      leftOut += 1;
    } else {
      // A figure that does not read: as the list had it.
      groups.push(request);
    }
  }
  const unitOf = (unit: ApprovalGroup) =>
    `${matchSetKey(unit.matchIds)}@${unit.amountValue === null ? '' : chargeableCents(unit.amountValue)}`;
  const changed =
    leftOut !== listedLeftOut ||
    groups.map(unitOf).join(';') !== listed.groups.map(unitOf).join(';');
  return { groups, leftOut, changed };
}
